export type BlogPost = {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  category: string;
  readTime: string;
  image?: string;
};

// Easy to add new blogs - just add to this array
export const BLOG_POSTS: BlogPost[] = [
  {
    id: "1",
    title: "5 Essential Ukulele Chords Every Beginner Should Master",
    excerpt: "Start your ukulele journey with these fundamental chords that form the foundation of countless songs.",
    content: `Learning the ukulele starts with mastering a few essential chords. These five chords - C, G, Am, F, and D - will unlock hundreds of songs and give you the confidence to play along with your favorite tunes.

## The Big Five Chords

### C Major (C)
The C chord is often the first chord beginners learn. Place your ring finger on the third fret of the A string. This chord sounds bright and happy, perfect for upbeat songs.

### G Major (G)
The G chord uses three fingers: index on the second fret of the C string, middle finger on the second fret of the A string, and ring finger on the third fret of the E string. It's a versatile chord that works in many progressions.

### A Minor (Am)
A minor is simple - just place your middle finger on the second fret of the G string. This chord adds emotional depth to your playing and is essential for creating contrast in songs.

### F Major (F)
The F chord uses two fingers: index on the first fret of the E string and middle finger on the second fret of the G string. It's a foundational chord in many popular songs.

### D Major (D)
The D chord requires three fingers on the second fret. It completes the essential chord set and opens up even more song possibilities.

## Practice Tips

1. **Start Slow**: Focus on clean chord changes rather than speed
2. **Use a Metronome**: This helps develop timing and rhythm
3. **Practice Daily**: Even 10-15 minutes a day makes a difference
4. **Learn Songs**: Apply these chords to real songs to stay motivated

With these five chords, you can play hundreds of popular songs. Start with simple progressions like C-G-Am-F, and you'll be playing recognizable tunes in no time!`,
    author: "Alex Martinez",
    date: "2024-01-15",
    category: "Beginner Tips",
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=800&h=600&fit=crop",
  },
  {
    id: "2",
    title: "Understanding Ukulele Sizes: Soprano, Concert, Tenor, and Baritone",
    excerpt: "A comprehensive guide to choosing the right ukulele size for your playing style and comfort.",
    content: `From the tiny soprano to the larger baritone, each ukulele size offers unique characteristics. Learn about the differences in sound, playability, and which size might be perfect for you.

## Soprano Ukulele
The smallest and most traditional size, soprano ukuleles are perfect for beginners and those who want that classic "ukulele sound." They're portable, affordable, and great for children or players with smaller hands.

## Concert Ukulele
Slightly larger than soprano, concert ukuleles offer more finger space while maintaining the traditional sound. They're a great middle ground for most players.

## Tenor Ukulele
Tenor ukuleles provide more room for complex fingerpicking and are popular among professional players. They have a richer, fuller sound and are excellent for solo performances.

## Baritone Ukulele
The largest size, baritone ukuleles are tuned differently (D-G-B-E) and sound more like a small guitar. They're perfect for guitarists transitioning to ukulele.

Choose the size that feels most comfortable in your hands and matches your musical goals!`,
    author: "Sarah Johnson",
    date: "2024-01-10",
    category: "Guides",
    readTime: "8 min read",
    image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&h=600&fit=crop",
  },
  {
    id: "3",
    title: "How to Read Ukulele Tablature: A Complete Guide",
    excerpt: "Master the art of reading ukulele tabs and unlock thousands of songs available online.",
    content: `Tablature is one of the easiest ways to learn songs on the ukulele. This guide will teach you everything you need to know about reading tabs, from basic notation to advanced techniques.

## Understanding Tab Lines
Ukulele tabs use four horizontal lines, each representing a string. The top line is the A string, followed by E, C, and G (bottom line).

## Reading Numbers
Numbers on the lines tell you which fret to press. For example, a "3" on the A string means press the third fret of the A string.

## Practice Makes Perfect
Start with simple songs and gradually work your way up to more complex arrangements.`,
    author: "Michael Chen",
    date: "2024-01-05",
    category: "Tutorials",
    readTime: "10 min read",
  },
  {
    id: "4",
    title: "Practice Routine for Busy Schedules: 15 Minutes a Day",
    excerpt: "You don't need hours of practice to improve. Learn how to make the most of short practice sessions.",
    content: `Consistency beats duration. Discover effective practice strategies that fit into your busy life and help you make steady progress even with limited time.

## The 15-Minute Routine
Break your practice into focused segments: 5 minutes of warm-up, 5 minutes of new material, and 5 minutes of review.`,
    author: "Emma Rodriguez",
    date: "2023-12-28",
    category: "Practice Tips",
    readTime: "6 min read",
  },
  {
    id: "5",
    title: "Top 10 Ukulele Songs for Beginners",
    excerpt: "Start playing recognizable songs right away with this curated list of beginner-friendly tunes.",
    content: `These songs use simple chord progressions and are perfect for building confidence. From pop hits to classics, there's something for everyone on this list.`,
    author: "David Thompson",
    date: "2023-12-20",
    category: "Song Lists",
    readTime: "7 min read",
  },
  {
    id: "6",
    title: "Maintaining Your Ukulele: Care Tips for Longevity",
    excerpt: "Keep your instrument in perfect condition with these essential maintenance tips.",
    content: `Proper care ensures your ukulele sounds great for years. Learn about humidity control, string changes, cleaning, and storage to protect your investment.`,
    author: "Lisa Park",
    date: "2023-12-15",
    category: "Care & Maintenance",
    readTime: "9 min read",
  },
];

